// ============ LOCAL EMOTION ANALYSIS ============
export function analyzeMessage(text, tone, intent) {
  const t = text.toLowerCase();
  const has = (p) => p.test(t);
  const cnt = (p) => (t.match(p) || []).length;
  let risk = 0;
  const chips = [];

  const profanity = has(/\b(fuck|shit|bitch|ass|damn|crap|dick|piss|bastard|asshole|wtf|stfu|bullshit|dumbass|motherfuck|cock|cunt)\b/) || has(/f+u+c+k+|s+h+i+t+|b+i+t+c+h+/);
  const slurs = has(/\b(retard|idiot|moron|loser|trash|scum|worthless|piece of shit|go to hell|kill yourself|die)\b/);
  const agg = has(/\b(furious|outraged|ridiculous|unacceptable|pathetic|useless|incompetent|are you serious|what the hell|you people|never again|this is a joke)\b/) || cnt(/!/g) >= 3;
  const guilt = has(/\b(after everything|i've done for you|you never|you always|you promised|let me down|disappointed in you)\b/);
  const pagg = has(/\b(as i (said|mentioned|noted)|per my (last|previous)|clearly you|obviously|apparently you|i shouldn't have to)\b/);
  const threat = has(/\b(if you don't|or else|legal action|lawyer|escalate|report you|take this further)\b/);
  const frust = has(/\b(still not|keeps happening|again and again|broken|failed|every time|how many times)\b/);
  const over = cnt(/\bsorry\b/gi) >= 2;
  const uncert = cnt(/\bmaybe\b|\bpossibly\b|\bperhaps\b|\bi guess\b/gi) >= 2;
  const grateful = has(/\b(thank you|appreciate|grateful|amazing|great job|well done)\b/);
  const warm = has(/\b(hope you|looking forward|happy to|would love|means a lot|excited)\b/);
  const demanding = has(/\b(need this now|asap|urgent|immediately|right away|deadline)\b/);
  const dismissive = has(/\b(whatever|fine|don't care|not my problem|figure it out|deal with it)\b/);
  const sarcastic = has(/\b(oh great|wonderful|thanks a lot|sure thing|good luck with that|brilliant)\b/) && (has(/\b(not|never|barely|hardly)\b/) || cnt(/!/g) >= 2);

  if (slurs) { risk += 70; chips.push({ color: 'red', label: 'Harmful language' }); }
  if (profanity) { risk += 50; chips.push({ color: 'red', label: 'Profanity detected' }); }
  if (threat) { risk += 55; chips.push({ color: 'red', label: 'Threatening' }); }
  if (agg) { risk += 45; chips.push({ color: 'red', label: 'Aggressive' }); }
  if (guilt) { risk += 38; chips.push({ color: 'red', label: 'Guilt-tripping' }); }
  if (sarcastic) { risk += 30; chips.push({ color: 'orange', label: 'Sarcastic' }); }
  if (pagg) { risk += 28; chips.push({ color: 'orange', label: 'Passive-aggressive' }); }
  if (dismissive) { risk += 25; chips.push({ color: 'orange', label: 'Dismissive' }); }
  if (demanding) { risk += 20; chips.push({ color: 'orange', label: 'Demanding' }); }
  if (frust) { risk += 22; chips.push({ color: 'orange', label: 'Frustrated' }); }
  if (over) { risk += 12; chips.push({ color: 'yellow', label: 'Over-apologizing' }); }
  if (uncert) { risk += 12; chips.push({ color: 'yellow', label: 'Uncertain' }); }
  if (grateful) { risk = Math.max(0, risk - 18); chips.push({ color: 'green', label: 'Appreciative' }); }
  if (warm) { risk = Math.max(0, risk - 12); chips.push({ color: 'green', label: 'Warm' }); }

  if (intent === 'de-escalate' && risk > 30) risk += 10;
  if (intent === 'motivate' && (agg || threat)) risk += 15;
  if (intent === 'apologize' && !over && !has(/\bsorry\b/i)) chips.push({ color: 'yellow', label: 'Missing apology' });

  risk = Math.min(100, Math.max(0, risk));

  let signal, label, feel;
  if (risk >= 65) { signal = 'red'; label = 'High conflict risk'; feel = 'Receiver will feel attacked and defensive.'; }
  else if (risk >= 32) { signal = 'orange'; label = 'Some tension detected'; feel = 'Receiver may sense frustration or pressure.'; }
  else if (risk >= 12) { signal = 'yellow'; label = 'Minor signals'; feel = 'Some signals slightly affect how this lands.'; }
  else {
    signal = 'green';
    label = grateful ? 'Appreciative & warm' : warm ? 'Warm & professional' : 'Clear & neutral';
    feel = grateful ? 'Receiver will feel valued and recognized.' : warm ? 'Receiver will feel welcomed and respected.' : 'Receiver will experience this as clear and respectful.';
  }

  return { signal, label, feel, chips: chips.slice(0, 6), risk, piScore: Math.max(0, Math.min(100, 100 - risk)), hasProfanity: profanity || slurs };
}

// ============ TONES & INTENTS DATA ============
export const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'warm', label: 'Warm' },
  { id: 'assertive', label: 'Assertive' },
  { id: 'concise', label: 'Concise' },
  { id: 'confident', label: 'Confident' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'empathetic', label: 'Empathetic' },
  { id: 'diplomatic', label: 'Diplomatic' },
  { id: 'casual', label: 'Casual' },
  { id: 'urgent', label: 'Urgent' },
];

export const INTENT_CATEGORIES = [
  {
    label: 'Common',
    intents: [
      { id: 'inform', label: 'Inform' }, { id: 'request', label: 'Request' },
      { id: 'persuade', label: 'Persuade' }, { id: 'follow-up', label: 'Follow up' },
      { id: 'clarify', label: 'Clarify' }, { id: 'confirm', label: 'Confirm' },
      { id: 'remind', label: 'Remind' }, { id: 'introduce', label: 'Introduce' },
    ],
  },
  {
    label: 'Relationship',
    intents: [
      { id: 'apologize', label: 'Apologize' }, { id: 'thank', label: 'Thank' },
      { id: 'congratulate', label: 'Congratulate' }, { id: 'empathize', label: 'Empathize' },
      { id: 'encourage', label: 'Encourage' }, { id: 'comfort', label: 'Comfort' },
      { id: 'welcome', label: 'Welcome' }, { id: 'farewell', label: 'Say goodbye' },
    ],
  },
  {
    label: 'Leadership',
    intents: [
      { id: 'motivate', label: 'Motivate' }, { id: 'feedback', label: 'Give feedback' },
      { id: 'delegate', label: 'Delegate' }, { id: 'set-boundary', label: 'Set boundary' },
      { id: 'hold-accountable', label: 'Hold accountable' }, { id: 'coach', label: 'Coach' },
      { id: 'announce', label: 'Announce' },
    ],
  },
  {
    label: 'Difficult',
    intents: [
      { id: 'decline', label: 'Decline' }, { id: 'de-escalate', label: 'De-escalate' },
      { id: 'negotiate', label: 'Negotiate' }, { id: 'confront', label: 'Confront' },
      { id: 'disagree', label: 'Disagree' }, { id: 'escalate', label: 'Escalate' },
      { id: 'warn', label: 'Warn' }, { id: 'end-relationship', label: 'End relationship' },
    ],
  },
];

export const CALM_QUOTES = [
  { quote: "Speak when you are angry and you will make the best speech you will ever regret.", author: "Ambrose Bierce" },
  { quote: "In a gentle way, you can shake the world.", author: "Mahatma Gandhi" },
  { quote: "The best fighter is never angry.", author: "Lao Tzu" },
  { quote: "Between stimulus and response there is a space. In that space is our power to choose our response.", author: "Viktor Frankl" },
  { quote: "Holding onto anger is like drinking poison and expecting the other person to die.", author: "Buddha" },
  { quote: "When anger rises, think of the consequences.", author: "Confucius" },
];

export const SAMPLE_MESSAGES = [
  { label: 'Aggressive email', text: 'This is absolutely ridiculous! I told you three times already and nothing has changed. Are you even reading my emails? Fix this NOW or I will escalate.' },
  { label: 'Over-apologizing', text: 'Sorry sorry, I know this is probably a bad time. Maybe I should not have sent this, but I was just wondering if perhaps you might possibly have time to look at my proposal? Sorry to bother you.' },
  { label: 'Professional', text: 'Thank you for sharing the quarterly results. I appreciate the detailed breakdown and would love to schedule a brief call to discuss the growth strategy for Q2. Looking forward to your thoughts.' },
];

export const SAMPLE_PROMPTS = [
  { label: 'Vague request', text: 'write me a sales email' },
  { label: 'Rough idea', text: 'make a landing page copy for my saas product' },
  { label: 'Simple query', text: 'explain machine learning' },
];

export const AI_TARGETS = [
  { id: 'chatgpt', label: 'ChatGPT' }, { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' }, { id: 'perplexity', label: 'Perplexity' },
  { id: 'copilot', label: 'MS Copilot' }, { id: 'claude-code', label: 'Claude Code' },
  { id: 'cursor', label: 'Cursor' }, { id: 'openai', label: 'OpenAI' },
  { id: 'emergent', label: 'Emergent' }, { id: 'bolt', label: 'Bolt.new' },
  { id: 'custom', label: '+ Any AI' },
];

export const TASK_TYPES = [
  { id: 'general', label: 'General' },
  { id: 'writing', label: 'Writing' },
  { id: 'coding', label: 'Coding' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'research', label: 'Research' },
  { id: 'brainstorm', label: 'Brainstorm' },
  { id: 'email', label: 'Email' },
  { id: 'strategy', label: 'Strategy' },
  { id: 'debug', label: 'Debug' },
  { id: 'review', label: 'Code Review' },
];
